import { faCircleExclamation } from '@fortawesome/pro-solid-svg-icons';
import {
    fetchAgreementsThunk,
    persistAgreementSignaturesUnlessDepositRequiredThunk
} from 'actions/agreements/AgreementsActions';
import { addAlertThunk } from 'actions/alerts/AlertsActions';
import { BookingError, BookingErrorType } from 'actions/bookings/BookingsErrors';
import { AgreementCard } from 'components/Agreements/Card/AgreementCard';
import { AgreementModal } from 'components/Agreements/SignModal/AgreementSignModal';
import { Error } from 'components/Form/Error/Error';
import ErrorList from 'components/Form/ErrorList/ErrorList';
import { Input } from 'components/Form/Input/Input';
import { LeftColumn } from 'components/Layout/TwoColumns/LeftColumn/LeftColumn';
import { RightColumn } from 'components/Layout/TwoColumns/RightColumn/RightColumn';
import { TwoColumnsLayout } from 'components/Layout/TwoColumns/TwoColumnsLayout';
import { MarketplaceInfo } from 'components/Marketplace/Info/MarketplaceInfo';
import { SmsMarketingConsent } from 'components/Customer/SmsMarketingConsent/SmsMarketingConsent';
import { Alert } from 'components/UI/Alert/Alert';
import { Button } from 'components/UI/Button/Button';
import { Spinner } from 'components/UI/Spinner/Spinner';
import { updateCustomerSmsMarketingOptInThunk } from 'actions/customer/CustomerActions';
import { useMarketplace } from 'hooks/useMarketplace';
import React from 'react';
import { useSelector } from 'react-redux';
import { getActiveAgreements, getAgreementLoading } from 'reducers/agreements/AgreementsSelectors';
import { getSortedPaymentMethods } from 'reducers/paymentMethods/PaymentMethodsSelectors';
import { bookingRequiresApproval } from 'utils/serviceUtils';
import { hasValidCardOnFile } from 'utils/paymentMethodUtils';
import { dispatch } from 'store';
import { Agreement, PendingAgreementSignature } from 'types/Agreement';
import { createBookingThunk } from 'actions/onlineBooking/actions/OnlineBookingBookingActions';
import { recoverWalkingCodedBookingErrorThunk } from 'actions/onlineBooking/actions/OnlineBookingWalkingActions';
import {
    addOnlineBookingAgreementSignature,
    navigateAfterOnlineBookingCreatedThunk,
    navigateToStep,
    setOnlineBookingNotes
} from 'actions/onlineBooking/actions/OnlineBookingFlowActions';
import { OnlineBookingHeader } from 'components/OnlineBooking/Header/OnlineBookingHeader';
import { OnlineBookingResume } from 'components/OnlineBooking/Resume/OnlineBookingResume';
import {
    getOnlineBookingCustomer,
    getOnlineBookingDepositExceedsPrice,
    getOnlineBookingDepositRequired,
    getOnlineBookingState
} from 'reducers/onlineBooking/OnlineBookingSelectors';
import { OnlineBookingStep, isOnlineBookingSessionItem } from 'types/OnlineBooking';
import { resolveSessionBookingErrorTarget } from 'types/Walking';

export const OnlineBookingAgreementsStep: React.FC = () => {
    const marketplace = useMarketplace();
    const customer = useSelector(getOnlineBookingCustomer);

    const isAgreementsLoading = useSelector(getAgreementLoading);
    const agreements = useSelector(getActiveAgreements);
    const {
        isPending,
        agreementSignatures,
        notes: onlineBookingNotes,
        bookingItems
    } = useSelector(getOnlineBookingState);
    const isDepositRequired = useSelector(getOnlineBookingDepositRequired);
    const depositExceedsPrice = useSelector(getOnlineBookingDepositExceedsPrice);
    const paymentMethods = useSelector(getSortedPaymentMethods);

    const [notes, setNotes] = React.useState(onlineBookingNotes);
    const [smsMarketingOptIn, setSmsMarketingOptIn] = React.useState(false);
    const [isSavingSignatures, setIsSavingSignatures] = React.useState(false);

    const isSubmitting = isPending || isSavingSignatures;

    const requiresApproval = bookingRequiresApproval(
        bookingItems,
        marketplace.onlineBookingPreferences
    );

    const needsCardOnFileStep =
        marketplace.settings.cardOnFileEnabled && !hasValidCardOnFile(paymentMethods);
    const willGoToPayment = isDepositRequired || needsCardOnFileStep;

    const isDepositExceedsServicePrice = isDepositRequired && depositExceedsPrice;

    const submitLabel = willGoToPayment
        ? 'Continue'
        : requiresApproval
        ? 'Request Appointment'
        : 'Book Now';

    const isValid = React.useMemo(() => {
        return (
            !isPending && !isAgreementsLoading && agreementSignatures.length === agreements.length
        );
    }, [agreementSignatures, agreements, isPending, isAgreementsLoading]);

    const backHandler = () => {
        dispatch(navigateToStep(OnlineBookingStep.ServiceCategory));
    };

    const submitHandler = async () => {
        await dispatch(setOnlineBookingNotes(notes));

        if (customer.id) {
            // Best-effort: persisting consent must not block the booking request.
            try {
                await dispatch(
                    updateCustomerSmsMarketingOptInThunk(customer.id, smsMarketingOptIn)
                );
            } catch {
                // Ignore — the appointment request continues regardless.
            }
        }

        await dispatch(createBookingThunk())
            .catch(error => {
                if (error instanceof BookingError && error.type === BookingErrorType.Coded) {
                    const sessionTarget = resolveSessionBookingErrorTarget(
                        error.details,
                        bookingItems.filter(isOnlineBookingSessionItem)
                    );

                    if (sessionTarget) {
                        dispatch(
                            recoverWalkingCodedBookingErrorThunk(
                                sessionTarget.businessArea,
                                sessionTarget.detail.code ?? error.code,
                                sessionTarget.detail.appointmentUuid
                            )
                        );
                        return;
                    }
                }

                if (error instanceof BookingError) {
                    dispatch(addAlertThunk(error.message || 'Oops, something went wrong.'));
                }
                return;
            })
            .then(async booking => {
                if (!booking) return;

                setIsSavingSignatures(true);
                await dispatch(persistAgreementSignaturesUnlessDepositRequiredThunk());

                return dispatch(navigateAfterOnlineBookingCreatedThunk());
            });
    };

    React.useEffect(() => {
        dispatch(fetchAgreementsThunk(Number(customer.id)));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <TwoColumnsLayout header={<OnlineBookingHeader onBack={backHandler} />}>
            <LeftColumn contentClassName="flex flex-col flex-1">
                <h1 className="h1 underlined w-full mb-6">Booking notes</h1>

                {isDepositExceedsServicePrice && (
                    <Error
                        title="Your appointment requires a deposit before confirmation. The deposit exceeds the service price, to proceed with the booking please contact the marketplace."
                        className="mb-5"
                    />
                )}

                {isSubmitting && (
                    <div className="w-full text-center">
                        <Spinner size={50} />
                    </div>
                )}

                {!isSubmitting && !isDepositExceedsServicePrice && (
                    <>
                        <ErrorList className="mb-6" />

                        <Input
                            multiline
                            value={notes}
                            placeholder="Add any important notes & how you heard about us here!"
                            onChange={e => setNotes(e.target.value)}
                            onBlur={() => {
                                if (notes !== onlineBookingNotes) {
                                    dispatch(setOnlineBookingNotes(notes));
                                }
                            }}
                        />

                        {marketplace.settings.customDisclaimerEnabled &&
                            marketplace.settings.customDisclaimerMessage.trim().length > 0 && (
                                <Alert
                                    variant="warning"
                                    icon={faCircleExclamation}
                                    className="mt-6"
                                >
                                    <strong>Disclaimer</strong>
                                    <br />
                                    {marketplace.settings.customDisclaimerMessage}
                                </Alert>
                            )}

                        {agreements.length > 0 && <AgreementsForm agreements={agreements} />}
                    </>
                )}
            </LeftColumn>

            <RightColumn
                poweredByCuddles
                footer={
                    <div className="flex flex-col gap-4 w-full">
                        <SmsMarketingConsent
                            checked={smsMarketingOptIn}
                            onChange={setSmsMarketingOptIn}
                        />

                        <Button
                            fullWidth
                            disabled={!isValid || isDepositExceedsServicePrice || isSubmitting}
                            loading={isSubmitting || isAgreementsLoading}
                            onClick={submitHandler}
                        >
                            {submitLabel}
                        </Button>
                    </div>
                }
            >
                <div className="flex flex-col gap-5">
                    <MarketplaceInfo
                        inverted
                        marketplace={marketplace}
                        className="hide-on-mobile-and-tablet"
                    />

                    <OnlineBookingResume />
                </div>
            </RightColumn>
        </TwoColumnsLayout>
    );
};

const AgreementsForm = ({ agreements }: { agreements: Array<Agreement> }) => {
    const { agreementSignatures } = useSelector(getOnlineBookingState);

    const [selectedAgreement, setSelectedAgreement] = React.useState<Agreement | null>(null);

    const signAgreementHandler = (signature: PendingAgreementSignature) => {
        dispatch(addOnlineBookingAgreementSignature(signature));
        setSelectedAgreement(null);
    };

    return (
        <>
            <h1 className="h1 underlined w-full mt-6 mb-5">Agreements</h1>

            <div className="w-full flex flex-col gap-4">
                {agreements.map(agreement => (
                    <AgreementCard
                        key={agreement.id}
                        agreement={agreement}
                        onClick={setSelectedAgreement}
                        checked={agreementSignatures.some(
                            signature => signature.agreementId === agreement.id
                        )}
                    />
                ))}
            </div>

            {selectedAgreement && (
                <AgreementModal
                    open={!!selectedAgreement?.id}
                    agreement={selectedAgreement}
                    onClose={() => setSelectedAgreement(null)}
                    onSign={signAgreementHandler}
                />
            )}
        </>
    );
};
